import React from 'react'
import { useTranslation } from 'react-i18next'
import {Row, Col, Layout, Carousel} from 'antd'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {TerminalType} from '@/constants'
import {LoadingState} from '@/types'
import {
    TransactionsCardWithBarChartContainer,
    TransactionSummaryListContainer,
    TTransactionsCardWithBarChartContainerStore,
    UserGuideStoreSymbol
} from '@/components/containers'
import {AverageMetrics} from '@/pages/Overview/components/AverageMetrics'
import {CarouselWidgetItem} from '@/pages/Overview/components/CarouselWidgetItem'
import {getOverviewCarouselItems} from '@/constants/overview-carousel'
import styles from './styles.scss'
import {TUserGuideStore} from '@/components/containers/UserGuideContainer/TUserGuideStore'
import {ALL_POS_TRANS_CHART, POS_CHARGED_TRANS_COMPARE_CHART} from '@/constants/playwright-ids'

export const PosTransactionSummaryStoreSymbol = Symbol('PosTransactionSummaryStore')
export const PosChargedTransactionsCardWithBarChartContainerStoreSymbol = Symbol(
    'PosChargedTransactionsCardWithBarChartContainerStore'
)
export const PosAllTransactionsComparisonCardWithBarChartContainerStoreSymbol = Symbol(
    'PosAllTransactionsComparisonCardWithBarChartContainerStore'
)

const OverviewPosPaymentsAnalytics: React.FC = observer(() => {
    const { t } = useTranslation()
    const transactionStore = useInjection<TTransactionsCardWithBarChartContainerStore>(
        PosChargedTransactionsCardWithBarChartContainerStoreSymbol
    )
    const {setIsModalVisible} = useInjection<TUserGuideStore>(UserGuideStoreSymbol)
    const carouselItems = getOverviewCarouselItems(t, setIsModalVisible)

    return (
        <>
            <Layout.Content className={styles.body}>
                <Row gutter={[16, 16]}>
                    <Col span={24} xs={24}>
                        <TransactionSummaryListContainer
                            type={TerminalType.POS}
                            injectableIdentifier={PosTransactionSummaryStoreSymbol}
                        />
                    </Col>
                    <Col span={12} xs={24} sm={24} md={24} lg={12}>
                        <TransactionsCardWithBarChartContainer
                            title={t('Overview.allPosTransactions')}
                            injectableIdentifier={
                                PosChargedTransactionsCardWithBarChartContainerStoreSymbol
                            }
                            data-test-id={ALL_POS_TRANS_CHART}
                        />
                    </Col>
                    <Col span={12} xs={24} sm={24} md={24} lg={12}>
                        <AverageMetrics
                            isLoading={transactionStore.dataLoadingState === LoadingState.LOADING}
                            transactionValue={transactionStore.transactionValue}
                            dailyValue={transactionStore.dailyValue}
                            transactionsPerDay={transactionStore.transactionsPerDay}
                            currency={transactionStore.currency}
                        />
                    </Col>
                    <Col span={12} xs={24} sm={24} md={24} lg={12}>
                        <TransactionsCardWithBarChartContainer
                            title={t('Overview.chargedTransactionsComparison')}
                            injectableIdentifier={
                                PosAllTransactionsComparisonCardWithBarChartContainerStoreSymbol
                            }
                            data-test-id={POS_CHARGED_TRANS_COMPARE_CHART}
                        />
                    </Col>
                    <Col span={12} xs={24} sm={24} md={24} lg={12}>
                        <Carousel autoplay>
                            {carouselItems.map((item, index) => (
                                <CarouselWidgetItem
                                    key={index}
                                    title={t(item.titleKey)}
                                    subtitle={t(item.subtitleKey)}
                                    description={t(item.descriptionKey)}
                                    linkText={t(item.linkTextKey)}
                                    href={item.href}
                                    onLinkClick={item.onLinkClick}
                                    features={item.features}
                                    backgroundColor={item.backgroundColor}
                                />
                            ))}
                        </Carousel>
                    </Col>
                </Row>
            </Layout.Content>
        </>
    )
})

export default OverviewPosPaymentsAnalytics
